import React, {memo, useState} from "react";
import PropTypes from "prop-types";
import {Box, IconButton, Typography} from "@mui/material";
import StyledTooltip from "../tooltips/StyledTooltip";
import DownloadIcon from "@mui/icons-material/Download";
import SyncIcon from "@mui/icons-material/Sync";
import SimpleFileIcon from "./SimpleFileIcon";
import {filesize} from "filesize";
import {useGeneratedDocumentChipStyles} from "./useGeneratedDocumentChipStyles";
import {spinAnimation} from "../../resources/styles/AnimationStyles";
import {isFinite} from "underscore";

const GeneratedDocumentChip = memo(({fileName, fileSize, onDownload}) => {
  const {classes} = useGeneratedDocumentChipStyles();
  const [isDownloading, setIsDownloading] = useState(false);

  if (!fileName || !isFinite(fileSize)) {
    return null;
  }

  const handleClick = async () => {
    if (isDownloading) {
      return;
    }
    setIsDownloading(true);
    try {
      await onDownload();
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box onClick={handleClick} className={classes.root} id="download-file">
      <SimpleFileIcon fileName={fileName} className={classes.icon} />
      <Box className={classes.textContainer} ml={1}>
        <Typography variant="body2" noWrap className={classes.fileName}>
          {fileName}
        </Typography>
        <Typography variant="caption" color="text.secondary" className={classes.fileSize}>
          {filesize(fileSize, {round: 1})}
        </Typography>
      </Box>
      <StyledTooltip title={isDownloading ? "Downloading..." : "Download"}>
        <IconButton
          size="small"
          disableRipple
          className={classes.downloadButton}
          id="download-file-button"
        >
          {isDownloading ?
            <SyncIcon fontSize="small" sx={spinAnimation} /> :
            <DownloadIcon fontSize="small" color="primary" />}
        </IconButton>
      </StyledTooltip>
    </Box>
  );
});

GeneratedDocumentChip.displayName = "GeneratedDocumentChip";

GeneratedDocumentChip.propTypes = {
  fileName: PropTypes.string,
  fileSize: PropTypes.number,
  onDownload: PropTypes.func.isRequired,
};

export default GeneratedDocumentChip;
